/**
 * Turg'un rejim: ε-NTU usuli asosida yechim, LMTD bilan o'zaro tekshiruv.
 *
 * ALGORITM
 *   1. Chiqish haroratlariga boshlang'ich taxmin.
 *   2. Har oqim uchun o'rtacha Cp = ΔH/ΔT (kirish–chiqish oralig'ida, entalpiya integrali).
 *   3. U: berilgan yoki korrelyatsiyadan (xossalar o'rtacha bulk haroratda).
 *   4. NTU, Cr → ε → Q → yangi chiqish haroratlari.
 *   5. Haroratlar 1e-9 K gacha o'zgarmay qolguncha 2-4 takrorlanadi.
 *   6. Natija LMTD orqali tekshiriladi: Q = U·A·F·ΔTlm (test to'liq mosligini talab qiladi).
 *
 * MODDIY BALANS: bir fazali oqimlar, massa sarfi o'zgarmas (kirish = chiqish).
 * ISSIQLIK BALANSI: Q = Wh·(h(Th,in) − h(Th,out)) = Wc·(h(Tc,out) − h(Tc,in)).
 * Atrofga issiqlik yo'qotish yo'q (adiabatik qobiq).
 */
import { getSubstance, meanHeatCapacity } from '../../properties';
import { warn, type SteadyResult, type Warning } from '../../types';
import { overallCoefficient, shellSideFilm, tubeSideFilm } from './correlations';
import { correctionFactor, effectiveness, lmtd, terminalDifferences } from './effectiveness';
import { heatTransferArea, validateParams, wallResistance } from './geometry';
import type {
  FilmCoefficient,
  HeatExchangerInputs,
  HeatExchangerOutputs,
  HeatExchangerParams,
} from './types';

export interface UResult {
  U: number;
  filmHot: FilmCoefficient | null;
  filmCold: FilmCoefficient | null;
  /** Ichki va tashqi film koeffitsientlari (Rw ni bo'lish uchun dinamikada kerak) */
  hInner: number | null;
  hOuter: number | null;
}

/** Umumiy koeffitsient U — rejimga qarab. */
export function computeU(
  p: HeatExchangerParams,
  inputs: HeatExchangerInputs,
  ThMean: number,
  TcMean: number,
  warnings?: Warning[],
): UResult {
  if (p.uMode === 'given') {
    return { U: p.uGiven as number, filmHot: null, filmCold: null, hInner: null, hOuter: null };
  }
  const hotIsTube = p.hotSide === 'tube';
  const filmHot = hotIsTube
    ? tubeSideFilm(p, inputs.hot, ThMean, warnings)
    : shellSideFilm(p, inputs.hot, ThMean, warnings);
  const filmCold = hotIsTube
    ? shellSideFilm(p, inputs.cold, TcMean, warnings)
    : tubeSideFilm(p, inputs.cold, TcMean, warnings);
  const hInner = hotIsTube ? filmHot.h : filmCold.h;
  const hOuter = hotIsTube ? filmCold.h : filmHot.h;
  const U = overallCoefficient(p, hInner, hOuter, wallResistance(p));
  return { U, filmHot, filmCold, hInner, hOuter };
}

export function validateInputs(inputs: HeatExchangerInputs): void {
  for (const [name, s] of [
    ['hot', inputs.hot],
    ['cold', inputs.cold],
  ] as const) {
    getSubstance(s.substance);
    if (!(s.Tin > 0)) throw new Error(`heat-exchanger: ${name}.Tin must be > 0 K`);
    if (!(s.massFlow >= 0)) throw new Error(`heat-exchanger: ${name}.massFlow must be ≥ 0`);
    if (!(s.pressure > 0)) throw new Error(`heat-exchanger: ${name}.pressure must be > 0`);
  }
}

const MAX_ITER = 100;
const TOL_K = 1e-9;

export function solveSteady(
  p: HeatExchangerParams,
  inputs: HeatExchangerInputs,
): SteadyResult<HeatExchangerOutputs> {
  validateParams(p);
  validateInputs(inputs);
  const warnings: Warning[] = [];
  const { hot, cold } = inputs;
  const A = heatTransferArea(p);

  if (hot.Tin < cold.Tin) warn(warnings, 'hx.hotColderThanCold', 'warning');

  // Sarf nol — issiqlik almashinuvi yo'q, apparat "ishlamaydi"
  if (hot.massFlow <= 0 || cold.massFlow <= 0) {
    warn(warnings, 'hx.noFlow', 'critical', {
      side: hot.massFlow <= 0 ? 'hot' : 'cold',
    });
    const cpHot = meanHeatCapacity(hot.substance, hot.phase, hot.Tin, hot.Tin, warnings);
    const cpCold = meanHeatCapacity(cold.substance, cold.phase, cold.Tin, cold.Tin, warnings);
    return {
      outputs: {
        Q: 0,
        hotTout: hot.Tin,
        coldTout: cold.Tin,
        U: p.uMode === 'given' ? (p.uGiven as number) : NaN,
        area: A,
        Chot: hot.massFlow * cpHot,
        Ccold: cold.massFlow * cpCold,
        Cmin: 0,
        Cmax: Math.max(hot.massFlow * cpHot, cold.massFlow * cpCold),
        Cr: 0,
        NTU: Infinity,
        effectiveness: NaN,
        lmtd: NaN,
        correctionFactor: 1,
        cpHot,
        cpCold,
        filmHot: null,
        filmCold: null,
      },
      warnings,
      diagnostics: { converged: true, iterations: 0, residual: 0 },
    };
  }

  // Boshlang'ich taxmin: 30% yaqinlashish
  const span = hot.Tin - cold.Tin;
  let ThOut = hot.Tin - 0.3 * span;
  let TcOut = cold.Tin + 0.3 * span;

  let converged = false;
  let iterations = 0;
  let residual = Infinity;
  let uRes: UResult = { U: NaN, filmHot: null, filmCold: null, hInner: null, hOuter: null };
  let cpHot = NaN;
  let cpCold = NaN;
  let Q = 0;
  let Chot = 0;
  let Ccold = 0;
  let NTU = 0;
  let eps = 0;

  for (iterations = 1; iterations <= MAX_ITER; iterations++) {
    cpHot = meanHeatCapacity(hot.substance, hot.phase, hot.Tin, ThOut, warnings);
    cpCold = meanHeatCapacity(cold.substance, cold.phase, cold.Tin, TcOut, warnings);
    Chot = hot.massFlow * cpHot;
    Ccold = cold.massFlow * cpCold;
    const Cmin = Math.min(Chot, Ccold);
    const Cmax = Math.max(Chot, Ccold);
    const Cr = Cmin / Cmax;

    uRes = computeU(p, inputs, 0.5 * (hot.Tin + ThOut), 0.5 * (cold.Tin + TcOut), warnings);
    NTU = (uRes.U * A) / Cmin;
    eps = effectiveness(p.arrangement, NTU, Cr);
    Q = eps * Cmin * span;

    const ThNew = hot.Tin - Q / Chot;
    const TcNew = cold.Tin + Q / Ccold;
    residual = Math.max(Math.abs(ThNew - ThOut), Math.abs(TcNew - TcOut));
    ThOut = ThNew;
    TcOut = TcNew;
    if (residual < TOL_K) {
      converged = true;
      break;
    }
  }

  const Cmin = Math.min(Chot, Ccold);
  const Cmax = Math.max(Chot, Ccold);
  const { dT1, dT2 } = terminalDifferences(p.arrangement, hot.Tin, ThOut, cold.Tin, TcOut);
  const dTlm = lmtd(Math.abs(dT1), Math.abs(dT2));
  const F = correctionFactor(p.arrangement, hot.Tin, ThOut, cold.Tin, TcOut);

  // Fizik diagnostika
  if (p.arrangement === 'shell-1-2' && TcOut > ThOut) {
    warn(warnings, 'hx.temperatureCross', 'warning', { F: Number(F.toFixed(3)) });
  }
  if (p.arrangement === 'shell-1-2' && F < 0.75) {
    warn(warnings, 'hx.lowCorrectionFactor', 'warning', { F: Number(F.toFixed(3)) });
  }
  if (Math.min(Math.abs(dT1), Math.abs(dT2)) < 1) warn(warnings, 'hx.closeApproach', 'info');
  if (NTU > 10) warn(warnings, 'hx.highNTU', 'info', { NTU: Number(NTU.toFixed(1)) });

  return {
    outputs: {
      Q,
      hotTout: ThOut,
      coldTout: TcOut,
      U: uRes.U,
      area: A,
      Chot,
      Ccold,
      Cmin,
      Cmax,
      Cr: Cmin / Cmax,
      NTU,
      effectiveness: eps,
      lmtd: dTlm,
      correctionFactor: F,
      cpHot,
      cpCold,
      filmHot: uRes.filmHot,
      filmCold: uRes.filmCold,
    },
    warnings,
    diagnostics: converged
      ? { converged, iterations, residual }
      : { converged, iterations: MAX_ITER, residual, message: 'solver.maxIterations' },
  };
}
